<template>
  <div class="user-content-section">
    <template v-if="user">
      <div class="page-sub-title user-sub-title">
        <h2>
          <i class="fa-title"></i>
          <div>{{ $t("profile") }}</div>
        </h2>
      </div>
      <div class="profile-list">
        <div class="profile-item">
          <div class="profile-title">
            <div class="courses-title-frame cart-title">
              <i class="fa-title"></i>
              <h2>
                <div>{{ $t("personal_info") }}</div>
              </h2>
              <svg
                class="invalid"
                v-if="isProfileIncomplete"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 192 512"
              >
                <path
                  fill="currentColor"
                  d="M20 424.229h20V279.771H20c-11.046 0-20-8.954-20-20V212c0-11.046 8.954-20 20-20h112c11.046 0 20 8.954 20 20v212.229h20c11.046 0 20 8.954 20 20V492c0 11.046-8.954 20-20 20H20c-11.046 0-20-8.954-20-20v-47.771c0-11.046 8.954-20 20-20zM96 0C56.235 0 24 32.235 24 72s32.235 72 72 72 72-32.235 72-72S135.764 0 96 0z"
                ></path>
              </svg>
            </div>
            <i class="fa-reset profile-icon"></i>
          </div>
          <form
            action="#"
            @change.prevent="submitUpdate"
            method="post"
            class="user-form user-form-input"
          >
            <div class="user-form-group">
              <label class="field-label">{{ $t('first_name') }}</label>
              <input
                type="text"
                :class="{ invalid: !$v.user.first_name.required && $v.user.first_name.$dirty }"
                v-model="user.first_name"
                class="user-profile-input"
                :placeholder="$t('first_name_placeholder')"
              />
              <div class="error" v-if="!$v.user.first_name.required && $v.user.first_name.$dirty">
                <div>{{ $t("first_name_required") }}</div>
              </div>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('last_name') }}</label>
              <input
                type="text"
                :class="{ invalid: !$v.user.last_name.required && $v.user.last_name.$dirty }"
                v-model="user.last_name"
                class="user-profile-input"
                :placeholder="$t('last_name_placeholder')"
              />
              <div class="error" v-if="!$v.user.last_name.required && $v.user.last_name.$dirty">
                <div>{{ $t("last_name_required") }}</div>
              </div>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('full_name') }}</label>
              <input
                type="text"
                :class="{ invalid: !$v.user.full_name.required && $v.user.full_name.$dirty }"
                v-model="user.full_name"
                class="user-profile-input"
                :placeholder="$t('full_name_placeholder')"
              />
              <div class="error" v-if="!$v.user.full_name.required && $v.user.full_name.$dirty">
                <div>{{ $t("full_name_required") }}</div>
              </div>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('birth_date') }}</label>
              <date-picker
                v-model="user.date_birth"
                format="YYYY-MM-DD"
                value-type="YYYY-MM-DD"
                type="date"
                :input-attr="{ class: 'user-profile-input' }"
                :placeholder="$t('birth_date_placeholder')"
                @change="submitUpdate"
                style="width: 100%"
              ></date-picker>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('phone') }}</label>
              <input
                type="text"
                :class="{ invalid: !$v.user.phone.required && $v.user.phone.$dirty }"
                v-model="user.phone"
                class="user-profile-input input-mask"
                v-maska="'+ 998 - ## - ### - ## - ##'"
                placeholder="+ 998 - __ - ___ - __ - __"
              />
              <div class="error" v-if="!$v.user.phone.required && $v.user.phone.$dirty">
                <div>{{ $t("phone_required_input") }}</div>
              </div>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('position') }}</label>
              <input
                type="text"
                v-model="user.position"
                class="user-profile-input"
                :placeholder="$t('position_placeholder')"
              />
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('branch') }}</label>
              <select
                v-model="user.branch"
                class="user-profile-input"
                @change="submitUpdate"
              >
                <option :value="null" disabled>{{ $t('branch_placeholder') }}</option>
                <option
                  v-for="branch in branches"
                  :key="branch.id"
                  :value="branch.id"
                >
                  {{ branch.name }}
                </option>
              </select>
            </div>

            <div class="user-form-group">
              <label class="field-label">{{ $t('reserve') }}</label>
              <select
                v-model="user.reserve"
                class="user-profile-input"
                @change="submitUpdate"
              >
                <option :value="null" disabled>{{ $t('reserve') }}</option>
                <option :value="false">{{ $t('reserve_no') }}</option>
                <option :value="true">{{ $t('reserve_yes') }}</option>
              </select>
            </div>
          </form>
        </div>

        <div class="profile-item">
          <div class="profile-title">
            <div class="courses-title-frame cart-title">
              <i class="fa-title"></i>
              <h2>
                <div>{{ $t("change_password") }}</div>
              </h2>
            </div>
            <i class="fa-reset profile-icon"></i>
          </div>
          <form action="#" method="post" class="user-form user-form-password">
            <div class="user-form-group" v-if="!old_password_check">
              <input
                type="password"
                v-model="old_password"
                class="user-profile-input"
                :placeholder="$t('old_password_placeholder')"
              />
              <div class="error" v-if="!$v.old_password.required && $v.old_password.$dirty">
                <div>{{ $t("password_required") }}</div>
              </div>
              <div class="error" v-if="!$v.old_password.minLength && $v.old_password.$dirty">
                <div>
                  {{ $t("password_min_length").replace("{min}", $v.password1.$params.minLength.min) }}
                </div>
              </div>
            </div>
            <div class="user-form-group" v-if="old_password_check">
              <input
                type="password"
                :class="{ invalid: !$v.password1.required && $v.password1.$dirty }"
                v-model="password1"
                class="user-profile-input"
                :placeholder="$t('new_password_placeholder')"
              />
              <div class="error" v-if="!$v.password1.required && $v.password1.$dirty">
                <div>{{ $t("password_required") }}</div>
              </div>
              <div class="error" v-if="!$v.password1.minLength && $v.password1.$dirty">
                <div>
                  {{ $t("password_min_length").replace("{min}", $v.password1.$params.minLength.min) }}
                </div>
              </div>
            </div>
            <div class="user-form-group" v-if="old_password_check">
              <input
                type="password"
                :class="{ invalid: !$v.password2.sameAsPassword && $v.password2.$dirty }"
                v-model="password2"
                class="user-profile-input"
                :placeholder="$t('repeat_password_placeholder')"
              />
              <div class="error" v-if="!$v.password2.sameAsPassword && $v.password2.$dirty">
                <div>{{ $t("passwords_not_match") }}</div>
              </div>
            </div>
            <label class="user-sub" v-if="!old_password_check">
              <input type="submit" @click.prevent="submitCheckPassword" />
              <div>{{ $t("verify") }}</div>
            </label>
            <label class="user-sub" v-else>
              <input type="submit" @click.prevent="submitNewPassword" />
              <div>{{ $t("confirm") }}</div>
            </label>
          </form>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/ru";
import { minLength, required, sameAs } from "vuelidate/lib/validators";

export default {
  title: "Profil",

  data() {
    return {
      old_password: "",
      old_password_check: false,
      password1: "",
      password2: "",
    };
  },

  computed: {
    user() {
      return this.$store.getters.getUserCredentials;
    },
    branches() {
      return this.$store.getters.getBranches || [];
    },
    isProfileIncomplete() {
      return (
        this.user.first_name === "" ||
        this.user.last_name === "" ||
        this.user.full_name === "" ||
        this.user.date_birth === "" ||
        this.user.login === ""
      );
    },
  },

  methods: {
    async submitUpdate() {
      const formData = new FormData();
      formData.append("image", "");
      formData.append("first_name", this.user.first_name);
      formData.append("last_name", this.user.last_name);
      formData.append("full_name", this.user.full_name);
      formData.append(
        "phone",
        this.user.phone.replace(/-|\s/g, "").replace(/[{()}]/g, ""),
      );
      formData.append("country", "Узбекистан");
      formData.append("date_birth", this.user.date_birth || "");
      if (this.user.branch) {
        formData.append("branch", this.user.branch);
      }
      formData.append("position", this.user.position || "");
      formData.append("reserve", this.user.reserve ? "true" : "false");

      try {
        await this.$store.dispatch("profileUpdate", formData);
      } catch (e) {
        console.log(e);
      }
    },

    async submitCheckPassword() {
      this.$v.old_password.$touch();
      if (this.$v.old_password.$invalid) return;

      try {
        await this.$store.dispatch("checkPassword", {
          old_password: this.old_password,
        });
        this.old_password_check = true;
      } catch (e) {
        this.old_password_check = false;
      }
      this.resetPasswordFields();
    },

    async submitNewPassword() {
      this.$v.$touch();
      if (this.$v.password1.$invalid || this.$v.password2.$invalid) return;

      try {
        await this.$store.dispatch("newPassword", {
          password1: this.password1,
          password2: this.password2,
        });
        this.old_password_check = false;
      } catch (e) {
        this.old_password_check = false;
      }
      this.resetPasswordFields();
    },

    resetPasswordFields() {
      this.old_password = "";
      this.password1 = "";
      this.password2 = "";
      this.$v.$reset();
      this.$v.user.$touch();
    },
  },

  created() {
    this.$v.user.$touch();
    this.$store.dispatch("getBranches");
  },

  validations: {
    user: {
      first_name: { required },
      last_name: { required },
      full_name: { required },
      date_birth: { required },
      phone: { required },
    },
    old_password: {
      required,
      minLength: minLength(6),
    },
    password1: {
      required,
      minLength: minLength(6),
    },
    password2: {
      sameAsPassword: sameAs("password1"),
    },
  },

  components: { DatePicker },
};
</script>

<style scoped>
.field-label {
  font-size: 14px;
  margin: 4px;
  color: #92a1d2;
}
</style>
